/**
 * stylePacks.js — the Style Pack System (Phase 1 of the spec).
 *
 * Each pack contains 4 named variants. Each variant is a typed
 * recipe controlling background, typography, color, pattern, and
 * contrast treatment. The render engine (MoodCard.jsx) consumes
 * `recipe` directly when present — falling back to the legacy
 * packTreatment() for cards created before this system landed.
 *
 * Six launch packs (Phase 1):
 *   1. cinematic         — atmospheric, dramatic, premium
 *   2. romantic          — intimate, expressive, warm
 *   3. minimal           — refined, timeless, calm
 *   4. bold_gradient     — modern, social-first, energetic
 *   5. faith             — hopeful, scripture-friendly, respectful
 *   6. vintage           — nostalgic, tactile, paper-led
 *
 * Deferred (Phase 2+): brutalist, polaroid, watercolor, cosmic,
 * playful, luxury, editorial, nature, celebration, street/urban,
 * soft_pastel, collage, futuristic, handwritten, seasonal,
 * heritage/cultural, wellness, meme/reaction, professional.
 */

/** @typedef {{
 *   id: string,
 *   label: string,
 *   hueLane: string,
 *   background: string,
 *   textColor: string,
 *   accentColor: string,
 *   labelColor: string,
 *   watermarkColor: string,
 *   titleFontClass: string,
 *   bodyFontClass: string,
 *   bodyWeightClass?: string,
 *   bodyTransformClass?: string,
 *   readability: "dark-bg"|"light-bg"|"busy-bg"|"minimal",
 *   pattern?: { family: string, opacity: number, placement: string },
 *   treatment?: { vignette?: number, grain?: number, scrim?: string }
 * }} StylePackRecipe
 */

// ── Tailwind font-family classes (added in tailwind.config.js) ──
//
//   font-display          → Playfair Display      (editorial serif)
//   font-display-modern   → DM Serif Display      (premium serif)
//   font-serif            → Cormorant Garamond    (existing — used for romantic/vintage)
//   font-serif-vintage    → Libre Baskerville     (heritage serif)
//   font-serif-formal     → Cinzel                (ceremonial serif)
//   font-serif-warm       → Fraunces              (warm display serif)
//   font-grotesk          → Space Grotesk         (modern sans)
//   font-condensed        → Bebas Neue            (condensed sans)
//   font-sans             → Outfit                (existing — universal supporting)

// =====================================================================
// 1. CINEMATIC — atmospheric, dramatic, premium
// =====================================================================
export const CINEMATIC = {
    id: "cinematic",
    label: "Cinematic",
    description: "Atmospheric, dramatic, premium. Like a film still.",
    variants: [
        {
            id: "golden-hour",
            label: "Golden Hour",
            hueLane: "honey-amber",
            background: "linear-gradient(180deg, #2a1810 0%, #4a2c1a 30%, #c97f3a 70%, #f4b574 100%)",
            textColor: "#FAF3E8",
            accentColor: "#F4B574",
            labelColor: "rgba(250,243,232,0.78)",
            watermarkColor: "rgba(250,243,232,0.42)",
            titleFontClass: "font-display italic",
            bodyFontClass: "font-display",
            bodyWeightClass: "font-normal",
            readability: "dark-bg",
            treatment: { vignette: 0.45, grain: 0.18 },
            motion: { type: "drift", intensity: "subtle" },
        },
        {
            id: "midnight-film",
            label: "Midnight Film",
            hueLane: "midnight-silver",
            // Soft starlight: two radials over a deep navy linear — gives the
            // sense of a film projected against a still night sky rather than
            // a flat gradient.
            background: "radial-gradient(60% 40% at 22% 18%, rgba(180,170,220,0.20) 0%, transparent 55%), radial-gradient(50% 35% at 78% 70%, rgba(140,130,200,0.15) 0%, transparent 55%), linear-gradient(180deg, #050714 0%, #0c1226 40%, #1c2440 100%)",
            textColor: "#E8E6F0",
            accentColor: "#C7C2D8",
            labelColor: "rgba(232,230,240,0.72)",
            watermarkColor: "rgba(232,230,240,0.32)",
            titleFontClass: "font-display-modern italic",
            bodyFontClass: "font-display-modern",
            readability: "dark-bg",
            treatment: { vignette: 0.55, grain: 0.22 },
            motion: { type: "breathe", intensity: "subtle" },
        },
        {
            id: "noir",
            label: "Noir",
            hueLane: "ink-ivory",
            // Pressed-light noir: tight radial spotlight bottom-center + a
            // single warm halo top-left to imply a window. Pure-black flat
            // backgrounds always read cheap; this adds atmosphere.
            background: "radial-gradient(45% 35% at 18% 14%, rgba(245,240,230,0.10) 0%, transparent 55%), radial-gradient(70% 45% at 50% 105%, rgba(225,75,58,0.14) 0%, transparent 60%), linear-gradient(180deg, #000000 0%, #0a0a0a 60%, #1a1a1a 100%)",
            textColor: "#F5F0E6",
            accentColor: "#E14B3A",
            labelColor: "rgba(245,240,230,0.7)",
            watermarkColor: "rgba(245,240,230,0.28)",
            titleFontClass: "font-condensed uppercase",
            bodyFontClass: "font-condensed uppercase",
            bodyWeightClass: "font-normal",
            readability: "dark-bg",
            treatment: { vignette: 0.65, grain: 0.32 },
        },
        {
            id: "soft-focus",
            label: "Soft Focus",
            hueLane: "rose-lavender",
            background: "radial-gradient(80% 60% at 20% 20%, #f0c8d8 0%, transparent 60%), radial-gradient(70% 60% at 80% 80%, #c8b8e0 0%, transparent 60%), linear-gradient(180deg, #f5e8ee 0%, #d8c8e0 100%)",
            textColor: "#2A1B30",
            accentColor: "#7C5A8C",
            labelColor: "rgba(42,27,48,0.68)",
            watermarkColor: "rgba(42,27,48,0.32)",
            titleFontClass: "font-serif italic",
            bodyFontClass: "font-serif",
            bodyWeightClass: "font-light italic",
            readability: "busy-bg",
            treatment: { vignette: 0.12, grain: 0.10 },
        },
    ],
};

// =====================================================================
// 2. ROMANTIC — intimate, expressive, warm
// =====================================================================
export const ROMANTIC = {
    id: "romantic",
    label: "Romantic",
    description: "Intimate, expressive, warm. A love letter rendered.",
    variants: [
        {
            id: "soft-blush",
            label: "Soft Blush",
            hueLane: "rose-cream",
            background: "radial-gradient(80% 80% at 30% 25%, #FBE3DA 0%, transparent 60%), radial-gradient(80% 80% at 75% 80%, #F4C9C2 0%, transparent 60%), linear-gradient(180deg, #FFF1EB 0%, #F8DDD3 100%)",
            textColor: "#3A1F1A",
            accentColor: "#B8736A",
            labelColor: "rgba(58,31,26,0.7)",
            watermarkColor: "rgba(58,31,26,0.36)",
            titleFontClass: "font-display italic",
            bodyFontClass: "font-serif",
            bodyWeightClass: "font-light italic",
            readability: "light-bg",
            treatment: { vignette: 0.10, grain: 0.10 },
            motion: { type: "breathe", intensity: "subtle" },
        },
        {
            id: "velvet-night",
            label: "Velvet Night",
            hueLane: "burgundy-champagne",
            background: "radial-gradient(100% 100% at 50% 0%, #3a0d18 0%, #1a050a 60%, #0a0205 100%)",
            textColor: "#F5E6D3",
            accentColor: "#E8C770",
            labelColor: "rgba(245,230,211,0.78)",
            watermarkColor: "rgba(245,230,211,0.35)",
            titleFontClass: "font-display-modern italic",
            bodyFontClass: "font-display-modern",
            readability: "dark-bg",
            treatment: { vignette: 0.55, grain: 0.18 },
        },
        {
            id: "modern-love",
            label: "Modern Love",
            hueLane: "cream-terracotta",
            background: "linear-gradient(180deg, #f4ebe0 0%, #e8c8b0 100%)",
            textColor: "#3D2418",
            accentColor: "#A85A3C",
            labelColor: "rgba(61,36,24,0.72)",
            watermarkColor: "rgba(61,36,24,0.32)",
            titleFontClass: "font-serif italic",
            bodyFontClass: "font-serif",
            bodyWeightClass: "font-light",
            readability: "light-bg",
            treatment: { vignette: 0.08, grain: 0.10 },
        },
        {
            id: "moonlit-romance",
            label: "Moonlit Romance",
            hueLane: "navy-silver",
            background: "radial-gradient(80% 60% at 50% 0%, #2a2440 0%, transparent 60%), linear-gradient(180deg, #0e0c20 0%, #1a1834 100%)",
            textColor: "#E8E0F0",
            accentColor: "#C8B8D8",
            labelColor: "rgba(232,224,240,0.72)",
            watermarkColor: "rgba(232,224,240,0.34)",
            titleFontClass: "font-serif italic",
            bodyFontClass: "font-serif",
            bodyWeightClass: "font-light italic",
            readability: "dark-bg",
            treatment: { vignette: 0.42, grain: 0.16 },
        },
    ],
};

// =====================================================================
// 3. MINIMAL — refined, timeless, calm
// =====================================================================
export const MINIMAL = {
    id: "minimal",
    label: "Minimal",
    description: "Refined, timeless, calm. Whitespace as the message.",
    variants: [
        {
            id: "pure",
            label: "Pure",
            hueLane: "white-charcoal",
            background: "#F8F6F2",
            textColor: "#1A1715",
            accentColor: "#8A857E",
            labelColor: "rgba(26,23,21,0.55)",
            watermarkColor: "rgba(26,23,21,0.18)",
            titleFontClass: "font-grotesk",
            bodyFontClass: "font-grotesk",
            bodyWeightClass: "font-light",
            readability: "light-bg",
            treatment: { vignette: 0.04, grain: 0.04 },
        },
        {
            id: "warm-neutral",
            label: "Warm Neutral",
            hueLane: "sand-taupe",
            background: "linear-gradient(180deg, #efe6d8 0%, #d8c5a8 100%)",
            textColor: "#3a2818",
            accentColor: "#7a5a3a",
            labelColor: "rgba(58,40,24,0.62)",
            watermarkColor: "rgba(58,40,24,0.22)",
            titleFontClass: "font-display-modern italic",
            bodyFontClass: "font-display-modern",
            readability: "light-bg",
            treatment: { vignette: 0.10, grain: 0.10 },
        },
        {
            id: "gallery",
            label: "Gallery",
            hueLane: "ivory-muted-red",
            background: "#FAF7F2",
            textColor: "#181614",
            accentColor: "#C44545",
            labelColor: "rgba(24,22,20,0.65)",
            watermarkColor: "rgba(24,22,20,0.16)",
            titleFontClass: "font-serif italic",
            bodyFontClass: "font-serif",
            bodyWeightClass: "font-light",
            readability: "light-bg",
            treatment: { vignette: 0.05, grain: 0.06 },
        },
        {
            id: "minimal-dark",
            label: "Minimal Dark",
            hueLane: "charcoal-ivory",
            background: "radial-gradient(100% 80% at 50% 50%, #1f1d22 0%, #0c0b0e 100%)",
            textColor: "#F0EBE2",
            accentColor: "#C0B8AC",
            labelColor: "rgba(240,235,226,0.62)",
            watermarkColor: "rgba(240,235,226,0.20)",
            titleFontClass: "font-grotesk",
            bodyFontClass: "font-grotesk",
            bodyWeightClass: "font-light",
            readability: "dark-bg",
            treatment: { vignette: 0.32, grain: 0.10 },
        },
    ],
};

// =====================================================================
// 4. BOLD_GRADIENT — modern, social-first, energetic
// =====================================================================
export const BOLD_GRADIENT = {
    id: "bold_gradient",
    label: "Bold Gradient",
    description: "Modern, energetic, social-first. Made to stop the scroll.",
    variants: [
        {
            id: "sunset",
            label: "Sunset",
            hueLane: "orange-pink-purple",
            background: "linear-gradient(135deg, #ff6b35 0%, #f72585 40%, #7209b7 100%)",
            textColor: "#FFFFFF",
            accentColor: "#FFE8A0",
            labelColor: "rgba(255,255,255,0.85)",
            watermarkColor: "rgba(255,255,255,0.42)",
            titleFontClass: "font-grotesk",
            bodyFontClass: "font-grotesk",
            bodyWeightClass: "font-semibold",
            readability: "dark-bg",
            treatment: { vignette: 0.22, grain: 0.14 },
            motion: { type: "drift", intensity: "moderate" },
        },
        {
            id: "ocean",
            label: "Ocean",
            hueLane: "navy-teal-aqua",
            background: "linear-gradient(135deg, #0d1b3e 0%, #1d6e7e 50%, #38d4c5 100%)",
            textColor: "#FFFFFF",
            accentColor: "#FFE8A0",
            labelColor: "rgba(255,255,255,0.82)",
            watermarkColor: "rgba(255,255,255,0.38)",
            titleFontClass: "font-display-modern italic",
            bodyFontClass: "font-display-modern",
            readability: "dark-bg",
            treatment: { vignette: 0.25, grain: 0.12 },
        },
        {
            id: "electric",
            label: "Electric",
            hueLane: "violet-cobalt-magenta",
            background: "linear-gradient(135deg, #0a0014 0%, #4a00b8 35%, #d40087 70%, #ff2bc6 100%)",
            textColor: "#FFFFFF",
            accentColor: "#FFE8A0",
            labelColor: "rgba(255,255,255,0.85)",
            watermarkColor: "rgba(255,255,255,0.40)",
            titleFontClass: "font-condensed uppercase",
            bodyFontClass: "font-condensed uppercase",
            bodyWeightClass: "font-normal",
            readability: "dark-bg",
            treatment: { vignette: 0.30, grain: 0.16 },
            motion: { type: "shimmer", intensity: "moderate" },
        },
        {
            id: "fire",
            label: "Fire",
            hueLane: "crimson-amber",
            background: "radial-gradient(100% 100% at 50% 100%, #ffb800 0%, #ff5a1f 35%, #b8181f 70%, #3a0808 100%)",
            textColor: "#FFFFFF",
            accentColor: "#FFE8A0",
            labelColor: "rgba(255,255,255,0.82)",
            watermarkColor: "rgba(255,255,255,0.38)",
            titleFontClass: "font-grotesk",
            bodyFontClass: "font-grotesk",
            bodyWeightClass: "font-semibold",
            readability: "dark-bg",
            treatment: { vignette: 0.35, grain: 0.16 },
        },
    ],
};

// =====================================================================
// 5. FAITH — hopeful, scripture-friendly, respectful
// =====================================================================
export const FAITH = {
    id: "faith",
    label: "Faith & Inspiration",
    description: "Hopeful, scripture-friendly, respectful. A quiet morning.",
    variants: [
        {
            id: "morning-grace",
            label: "Morning Grace",
            hueLane: "warm-gold-sky",
            background: "linear-gradient(180deg, #2a4a6a 0%, #c89a4a 70%, #f5e0a8 100%)",
            textColor: "#FAF3E8",
            accentColor: "#FFE8A0",
            labelColor: "rgba(250,243,232,0.78)",
            watermarkColor: "rgba(250,243,232,0.38)",
            titleFontClass: "font-serif-vintage italic",
            bodyFontClass: "font-serif-vintage",
            bodyWeightClass: "font-normal",
            readability: "dark-bg",
            treatment: { vignette: 0.32, grain: 0.10 },
            motion: { type: "glow", intensity: "subtle" },
        },
        {
            id: "sacred-minimal",
            label: "Sacred Minimal",
            hueLane: "ivory-navy-gold",
            background: "linear-gradient(180deg, #f4ebd8 0%, #e8d8b8 100%)",
            textColor: "#1A2440",
            accentColor: "#A88838",
            labelColor: "rgba(26,36,64,0.72)",
            watermarkColor: "rgba(26,36,64,0.28)",
            titleFontClass: "font-display-modern italic",
            bodyFontClass: "font-display-modern",
            readability: "light-bg",
            treatment: { vignette: 0.08, grain: 0.10 },
        },
        {
            id: "scripture-journal",
            label: "Scripture Journal",
            hueLane: "parchment-olive",
            background: "linear-gradient(180deg, #f0e6d2 0%, #d8c8a8 100%)",
            textColor: "#2a1f10",
            accentColor: "#5a6a28",
            labelColor: "rgba(42,31,16,0.72)",
            watermarkColor: "rgba(42,31,16,0.28)",
            titleFontClass: "font-serif-vintage italic",
            bodyFontClass: "font-serif-vintage",
            readability: "light-bg",
            treatment: { vignette: 0.10, grain: 0.18 },
        },
        {
            id: "royal-scripture",
            label: "Royal Scripture",
            hueLane: "purple-navy-gold",
            // Gold-dust constellation over deep royal purple — a cathedral
            // ceiling at midnight. Layered radials give scripture cards a
            // proper sense of awe vs. a single linear gradient.
            background: "radial-gradient(35% 25% at 20% 15%, rgba(232,199,112,0.18) 0%, transparent 55%), radial-gradient(30% 22% at 80% 70%, rgba(232,199,112,0.14) 0%, transparent 55%), radial-gradient(25% 18% at 50% 40%, rgba(245,230,200,0.10) 0%, transparent 55%), linear-gradient(180deg, #0a0518 0%, #2a1a4a 50%, #4a2a6a 100%)",
            textColor: "#F5E6C8",
            accentColor: "#E8C770",
            labelColor: "rgba(245,230,200,0.78)",
            watermarkColor: "rgba(245,230,200,0.34)",
            titleFontClass: "font-serif-formal uppercase",
            bodyFontClass: "font-serif-formal",
            readability: "dark-bg",
            treatment: { vignette: 0.45, grain: 0.14 },
            motion: { type: "glow", intensity: "subtle" },
        },
    ],
};

// =====================================================================
// 6. VINTAGE — nostalgic, tactile, paper-led
// =====================================================================
export const VINTAGE = {
    id: "vintage",
    label: "Vintage",
    description: "Nostalgic, tactile, collected over time. Like found paper.",
    variants: [
        {
            id: "old-letter",
            label: "Old Letter",
            hueLane: "sepia-faded-brown",
            // Browning paper with ink stain in the corner + a soft fold-line
            // implication via a faint vertical highlight down the middle.
            background: "radial-gradient(15% 12% at 88% 8%, rgba(58,32,8,0.22) 0%, transparent 55%), radial-gradient(60% 100% at 50% 50%, rgba(255,240,200,0.18) 0%, transparent 65%), linear-gradient(180deg, #f4e8d0 0%, #d8b888 100%)",
            textColor: "#3a2008",
            accentColor: "#7a4818",
            labelColor: "rgba(58,32,8,0.78)",
            watermarkColor: "rgba(58,32,8,0.32)",
            titleFontClass: "font-serif italic",
            bodyFontClass: "font-serif",
            bodyWeightClass: "font-light italic",
            readability: "light-bg",
            treatment: { vignette: 0.22, grain: 0.36 },
        },
        {
            id: "retro-poster",
            label: "Retro Poster",
            hueLane: "mustard-faded-red",
            background: "linear-gradient(180deg, #e8b840 0%, #c84838 100%)",
            textColor: "#1a0808",
            accentColor: "#FFF1D8",
            labelColor: "rgba(26,8,8,0.78)",
            watermarkColor: "rgba(26,8,8,0.32)",
            titleFontClass: "font-serif-warm",
            bodyFontClass: "font-serif-warm",
            bodyWeightClass: "font-semibold",
            readability: "light-bg",
            treatment: { vignette: 0.18, grain: 0.28 },
        },
        {
            id: "heritage",
            label: "Heritage",
            hueLane: "deep-green-ivory",
            // Forest-green velvet with a single gold-leaf halo bottom-right —
            // an heirloom book closed on a side table, lamp glowing nearby.
            background: "radial-gradient(45% 35% at 78% 82%, rgba(200,168,72,0.18) 0%, transparent 55%), radial-gradient(60% 40% at 22% 18%, rgba(40,80,52,0.30) 0%, transparent 60%), radial-gradient(100% 80% at 50% 0%, #1a3a28 0%, #0a1a14 60%, #050a08 100%)",
            textColor: "#F0E8D0",
            accentColor: "#C8A848",
            labelColor: "rgba(240,232,208,0.78)",
            watermarkColor: "rgba(240,232,208,0.32)",
            titleFontClass: "font-serif-vintage italic",
            bodyFontClass: "font-serif-vintage",
            readability: "dark-bg",
            treatment: { vignette: 0.50, grain: 0.18 },
        },
        {
            id: "newspaper",
            label: "Newspaper",
            hueLane: "newsprint-charcoal",
            background: "#f0ebe0",
            textColor: "#181410",
            accentColor: "#a82828",
            labelColor: "rgba(24,20,16,0.72)",
            watermarkColor: "rgba(24,20,16,0.22)",
            titleFontClass: "font-serif-vintage",
            bodyFontClass: "font-serif-vintage",
            bodyWeightClass: "font-normal",
            readability: "light-bg",
            treatment: { vignette: 0.08, grain: 0.32 },
        },
    ],
};

// =====================================================================
// 7. BRUTALIST — raw, forceful, intelligent, unapologetic
// =====================================================================
export const BRUTALIST = {
    id: "brutalist",
    label: "Brutalist",
    description: "Raw, forceful, intelligent. Architecture in font form.",
    variants: [
        {
            id: "stark",
            label: "Stark",
            hueLane: "white-black-red",
            background: "#FAF7F2",
            textColor: "#0A0A0A",
            accentColor: "#E5263F",
            labelColor: "rgba(10,10,10,0.78)",
            watermarkColor: "rgba(10,10,10,0.32)",
            titleFontClass: "font-condensed uppercase",
            bodyFontClass: "font-condensed uppercase",
            bodyWeightClass: "font-normal",
            readability: "light-bg",
            treatment: { vignette: 0.04, grain: 0.06 },
        },
        {
            id: "concrete",
            label: "Concrete",
            hueLane: "cement-charcoal-hazard",
            background: "radial-gradient(80% 60% at 30% 20%, rgba(80,80,82,0.35) 0%, transparent 60%), linear-gradient(180deg, #3a3a3c 0%, #1e1e20 100%)",
            textColor: "#FAF7F2",
            accentColor: "#FF7A1A",
            labelColor: "rgba(250,247,242,0.72)",
            watermarkColor: "rgba(250,247,242,0.28)",
            titleFontClass: "font-grotesk",
            bodyFontClass: "font-grotesk",
            bodyWeightClass: "font-bold uppercase",
            readability: "dark-bg",
            treatment: { vignette: 0.30, grain: 0.28 },
        },
        {
            id: "warning-label",
            label: "Warning Label",
            hueLane: "yellow-black-red",
            background: "repeating-linear-gradient(135deg, #E5C100 0px, #E5C100 24px, #0A0A0A 24px, #0A0A0A 48px)",
            textColor: "#0A0A0A",
            accentColor: "#E5263F",
            labelColor: "rgba(10,10,10,0.85)",
            watermarkColor: "rgba(10,10,10,0.45)",
            titleFontClass: "font-condensed uppercase",
            bodyFontClass: "font-condensed uppercase",
            bodyWeightClass: "font-normal",
            readability: "busy-bg",
            treatment: { vignette: 0.05, grain: 0.10 },
        },
        {
            id: "manifesto",
            label: "Manifesto",
            hueLane: "cream-ink-red",
            background: "linear-gradient(180deg, #F2EBE0 0%, #E8DDC8 100%)",
            textColor: "#0A0A0A",
            accentColor: "#A8181F",
            labelColor: "rgba(10,10,10,0.78)",
            watermarkColor: "rgba(10,10,10,0.32)",
            titleFontClass: "font-grotesk",
            bodyFontClass: "font-grotesk",
            bodyWeightClass: "font-bold",
            readability: "light-bg",
            treatment: { vignette: 0.10, grain: 0.20 },
        },
    ],
};

// =====================================================================
// 8. POLAROID — personal, memory-led, casual, emotionally warm
// =====================================================================
export const POLAROID_V2 = {
    id: "polaroid",
    label: "Polaroid",
    description: "Personal, memory-led, like a photo someone kept in a drawer.",
    variants: [
        {
            id: "classic-frame",
            label: "Classic Frame",
            hueLane: "white-cream-faded",
            background: "radial-gradient(80% 60% at 50% 30%, rgba(240,230,210,0.95) 0%, rgba(220,210,190,0.85) 50%, rgba(180,170,160,0.75) 100%), #F4ECD8",
            textColor: "#2A1F10",
            accentColor: "#8B6A3A",
            labelColor: "rgba(42,31,16,0.75)",
            watermarkColor: "rgba(42,31,16,0.30)",
            titleFontClass: "font-serif italic",
            bodyFontClass: "font-serif",
            bodyWeightClass: "font-light",
            readability: "light-bg",
            treatment: { vignette: 0.32, grain: 0.32 },
        },
        {
            id: "memory-lane",
            label: "Memory Lane",
            hueLane: "sepia-rose-teal",
            background: "radial-gradient(60% 50% at 70% 25%, rgba(245,180,150,0.40) 0%, transparent 60%), radial-gradient(50% 40% at 25% 80%, rgba(120,160,150,0.30) 0%, transparent 60%), linear-gradient(180deg, #E8D4B8 0%, #C8A878 100%)",
            textColor: "#2A1808",
            accentColor: "#A85A3C",
            labelColor: "rgba(42,24,8,0.75)",
            watermarkColor: "rgba(42,24,8,0.30)",
            titleFontClass: "font-serif italic",
            bodyFontClass: "font-serif",
            bodyWeightClass: "font-light italic",
            readability: "light-bg",
            treatment: { vignette: 0.25, grain: 0.36 },
        },
        {
            id: "travel-note",
            label: "Travel Note",
            hueLane: "sky-sand-terracotta",
            background: "linear-gradient(180deg, #A8C8D8 0%, #E8D4A8 60%, #C88858 100%)",
            textColor: "#1A2438",
            accentColor: "#A8482A",
            labelColor: "rgba(26,36,56,0.78)",
            watermarkColor: "rgba(26,36,56,0.32)",
            titleFontClass: "font-serif-warm",
            bodyFontClass: "font-serif-warm",
            bodyWeightClass: "font-normal",
            readability: "light-bg",
            treatment: { vignette: 0.18, grain: 0.22 },
        },
        {
            id: "best-friends",
            label: "Best Friends",
            hueLane: "peach-lavender-aqua",
            background: "radial-gradient(60% 50% at 25% 20%, rgba(255,200,168,0.50) 0%, transparent 60%), radial-gradient(50% 50% at 78% 75%, rgba(200,180,235,0.45) 0%, transparent 60%), linear-gradient(180deg, #FFF1E8 0%, #F0E8F8 100%)",
            textColor: "#3A2848",
            accentColor: "#D45A8A",
            labelColor: "rgba(58,40,72,0.72)",
            watermarkColor: "rgba(58,40,72,0.28)",
            titleFontClass: "font-serif-warm",
            bodyFontClass: "font-serif-warm",
            bodyWeightClass: "font-normal",
            readability: "light-bg",
            treatment: { vignette: 0.10, grain: 0.18 },
        },
    ],
};

// =====================================================================
// 9. WATERCOLOR — artistic, gentle, expressive, handmade
// =====================================================================
export const WATERCOLOR_V2 = {
    id: "watercolor",
    label: "Watercolor",
    description: "Gentle, handmade, painted on damp paper.",
    variants: [
        {
            id: "floral-wash",
            label: "Floral Wash",
            hueLane: "blush-sage-lavender",
            background: "radial-gradient(45% 40% at 22% 18%, rgba(248,200,210,0.55) 0%, transparent 60%), radial-gradient(40% 38% at 78% 78%, rgba(180,200,170,0.50) 0%, transparent 62%), radial-gradient(35% 30% at 55% 50%, rgba(210,190,225,0.40) 0%, transparent 60%), #FBF5EE",
            textColor: "#3A2828",
            accentColor: "#A8688A",
            labelColor: "rgba(58,40,40,0.72)",
            watermarkColor: "rgba(58,40,40,0.30)",
            titleFontClass: "font-display-modern italic",
            bodyFontClass: "font-display-modern",
            readability: "light-bg",
            treatment: { vignette: 0.10, grain: 0.18 },
        },
        {
            id: "ocean-mist",
            label: "Ocean Mist",
            hueLane: "mist-teal-sea-gray",
            background: "radial-gradient(70% 50% at 30% 20%, rgba(180,210,225,0.55) 0%, transparent 60%), radial-gradient(60% 50% at 75% 80%, rgba(120,168,180,0.45) 0%, transparent 60%), linear-gradient(180deg, #E8F0F4 0%, #B8D0D8 100%)",
            textColor: "#0A2438",
            accentColor: "#386878",
            labelColor: "rgba(10,36,56,0.72)",
            watermarkColor: "rgba(10,36,56,0.28)",
            titleFontClass: "font-serif-vintage italic",
            bodyFontClass: "font-serif-vintage",
            bodyWeightClass: "font-normal",
            readability: "light-bg",
            treatment: { vignette: 0.12, grain: 0.16 },
        },
        {
            id: "sunset-bloom",
            label: "Sunset Bloom",
            hueLane: "coral-peach-rose",
            background: "radial-gradient(60% 50% at 25% 25%, rgba(255,180,150,0.60) 0%, transparent 62%), radial-gradient(55% 45% at 78% 75%, rgba(245,150,168,0.55) 0%, transparent 62%), linear-gradient(180deg, #FFF0E8 0%, #FBD8C8 100%)",
            textColor: "#3A1A20",
            accentColor: "#C84858",
            labelColor: "rgba(58,26,32,0.72)",
            watermarkColor: "rgba(58,26,32,0.30)",
            titleFontClass: "font-serif italic",
            bodyFontClass: "font-serif",
            bodyWeightClass: "font-light italic",
            readability: "light-bg",
            treatment: { vignette: 0.10, grain: 0.16 },
        },
        {
            id: "botanical-ink",
            label: "Botanical Ink",
            hueLane: "sage-olive-parchment",
            background: "radial-gradient(50% 40% at 20% 80%, rgba(150,168,120,0.35) 0%, transparent 60%), radial-gradient(40% 35% at 80% 20%, rgba(120,140,90,0.30) 0%, transparent 60%), linear-gradient(180deg, #F4ECD8 0%, #D8C8A8 100%)",
            textColor: "#1A2008",
            accentColor: "#5A6828",
            labelColor: "rgba(26,32,8,0.78)",
            watermarkColor: "rgba(26,32,8,0.32)",
            titleFontClass: "font-serif-vintage italic",
            bodyFontClass: "font-serif-vintage",
            readability: "light-bg",
            treatment: { vignette: 0.14, grain: 0.22 },
        },
    ],
};

// =====================================================================
// 10. PLAYFUL — bright, expressive, cheerful, highly shareable
// =====================================================================
export const PLAYFUL = {
    id: "playful",
    label: "Playful",
    description: "Bright, expressive, made to make someone grin.",
    variants: [
        {
            id: "confetti-pop",
            label: "Confetti Pop",
            hueLane: "coral-cobalt-yellow-mint",
            background: "radial-gradient(20% 15% at 18% 22%, #FFD83A 0%, transparent 55%), radial-gradient(18% 14% at 78% 72%, #38C8B0 0%, transparent 55%), radial-gradient(16% 12% at 28% 78%, #FF6A48 0%, transparent 55%), radial-gradient(15% 12% at 72% 22%, #4A78D8 0%, transparent 55%), linear-gradient(180deg, #FFF8EC 0%, #FFE8D8 100%)",
            textColor: "#1A0F18",
            accentColor: "#D03878",
            labelColor: "rgba(26,15,24,0.78)",
            watermarkColor: "rgba(26,15,24,0.32)",
            titleFontClass: "font-serif-warm",
            bodyFontClass: "font-serif-warm",
            bodyWeightClass: "font-bold",
            readability: "busy-bg",
            treatment: { vignette: 0.08, grain: 0.12 },
        },
        {
            id: "doodle",
            label: "Doodle",
            hueLane: "cream-teal-orange-lilac",
            background: "linear-gradient(180deg, #FFF8E8 0%, #FFE8D0 100%)",
            textColor: "#1A1418",
            accentColor: "#38A8A0",
            labelColor: "rgba(26,20,24,0.75)",
            watermarkColor: "rgba(26,20,24,0.30)",
            titleFontClass: "font-grotesk",
            bodyFontClass: "font-grotesk",
            bodyWeightClass: "font-normal",
            readability: "light-bg",
            treatment: { vignette: 0.06, grain: 0.14 },
        },
        {
            id: "bubble-gum",
            label: "Bubble Gum",
            hueLane: "pink-lavender-aqua",
            background: "radial-gradient(40% 35% at 25% 30%, rgba(255,168,200,0.70) 0%, transparent 60%), radial-gradient(35% 30% at 78% 70%, rgba(168,200,240,0.65) 0%, transparent 60%), linear-gradient(180deg, #FFEEF6 0%, #E8E0F8 100%)",
            textColor: "#3A1838",
            accentColor: "#E558A8",
            labelColor: "rgba(58,24,56,0.72)",
            watermarkColor: "rgba(58,24,56,0.30)",
            titleFontClass: "font-serif-warm",
            bodyFontClass: "font-serif-warm",
            bodyWeightClass: "font-bold",
            readability: "light-bg",
            treatment: { vignette: 0.08, grain: 0.14 },
        },
        {
            id: "comic-fun",
            label: "Comic Fun",
            hueLane: "yellow-red-cobalt",
            background: "radial-gradient(25% 20% at 78% 22%, #E5283C 0%, transparent 55%), radial-gradient(20% 16% at 22% 78%, #2858D8 0%, transparent 55%), linear-gradient(180deg, #FFE848 0%, #FFC828 100%)",
            textColor: "#0A0A0A",
            accentColor: "#0A0A0A",
            labelColor: "rgba(10,10,10,0.85)",
            watermarkColor: "rgba(10,10,10,0.42)",
            titleFontClass: "font-condensed uppercase",
            bodyFontClass: "font-condensed uppercase",
            bodyWeightClass: "font-normal",
            readability: "busy-bg",
            treatment: { vignette: 0.08, grain: 0.16 },
        },
    ],
};

// =====================================================================
// 11. LUXURY — premium, restrained, polished, aspirational
// =====================================================================
export const LUXURY = {
    id: "luxury",
    label: "Luxury",
    description: "Restrained, polished, like an heirloom invitation.",
    variants: [
        {
            id: "black-and-gold",
            label: "Black & Gold",
            hueLane: "black-champagne-ivory",
            background: "radial-gradient(40% 30% at 30% 20%, rgba(232,200,120,0.18) 0%, transparent 60%), radial-gradient(35% 28% at 75% 78%, rgba(232,200,120,0.14) 0%, transparent 60%), linear-gradient(180deg, #08070a 0%, #0e0c10 60%, #14111a 100%)",
            textColor: "#F0E6C8",
            accentColor: "#E8C870",
            labelColor: "rgba(240,230,200,0.78)",
            watermarkColor: "rgba(240,230,200,0.36)",
            titleFontClass: "font-serif-formal uppercase",
            bodyFontClass: "font-serif-formal",
            readability: "dark-bg",
            treatment: { vignette: 0.45, grain: 0.12 },
            motion: { type: "shimmer", intensity: "subtle" },
        },
        {
            id: "emerald",
            label: "Emerald",
            hueLane: "deep-green-gold-cream",
            background: "radial-gradient(50% 40% at 30% 25%, rgba(248,228,168,0.18) 0%, transparent 60%), linear-gradient(180deg, #04140a 0%, #0a2818 50%, #143828 100%)",
            textColor: "#F8E8C8",
            accentColor: "#C8A848",
            labelColor: "rgba(248,232,200,0.78)",
            watermarkColor: "rgba(248,232,200,0.34)",
            titleFontClass: "font-display italic",
            bodyFontClass: "font-display",
            readability: "dark-bg",
            treatment: { vignette: 0.42, grain: 0.14 },
        },
        {
            id: "ivory-elegance",
            label: "Ivory Elegance",
            hueLane: "ivory-taupe-gold",
            background: "radial-gradient(60% 50% at 50% 30%, rgba(255,248,232,1) 0%, rgba(232,220,200,1) 70%, rgba(200,184,160,1) 100%)",
            textColor: "#1A1814",
            accentColor: "#A8884A",
            labelColor: "rgba(26,24,20,0.72)",
            watermarkColor: "rgba(26,24,20,0.28)",
            titleFontClass: "font-serif italic",
            bodyFontClass: "font-serif",
            bodyWeightClass: "font-light",
            readability: "light-bg",
            treatment: { vignette: 0.12, grain: 0.14 },
        },
        {
            id: "royal-blue",
            label: "Royal Blue",
            hueLane: "navy-silver-white",
            background: "radial-gradient(40% 32% at 32% 22%, rgba(220,225,240,0.18) 0%, transparent 60%), linear-gradient(180deg, #050820 0%, #0a1438 50%, #142858 100%)",
            textColor: "#E8ECF8",
            accentColor: "#C8D0E8",
            labelColor: "rgba(232,236,248,0.78)",
            watermarkColor: "rgba(232,236,248,0.34)",
            titleFontClass: "font-display-modern italic",
            bodyFontClass: "font-display-modern",
            readability: "dark-bg",
            treatment: { vignette: 0.40, grain: 0.10 },
        },
    ],
};

// =====================================================================
// iter-84 — Viral Utility Templates
//
// Three meme-native formats from the strategy doc. Each pack has STRONG
// visual identity (mono fonts / condensed headlines / perforated edges)
// so the card immediately reads as the format even without the structured
// text. The LLM prompt is nudged per-pack so generated bodies fit the
// expected shape (ITEM/QTY/TOTAL · BREAKING NEWS · ticket fields).
// =====================================================================

// 12. RECEIPT — meme-native shopping/transaction format
export const RECEIPT = {
    id: "receipt",
    label: "Receipt",
    description: "Meme-native. ITEM · QTY · TOTAL. The voice of a transaction.",
    variants: [
        {
            id: "thermal-cream",
            label: "Thermal Cream",
            hueLane: "cream-mono",
            // Off-white thermal paper texture — soft beige with slight tonal noise.
            background: "linear-gradient(180deg, #FBF6E8 0%, #F2EAD3 100%)",
            textColor: "#181410",
            accentColor: "#9C2A1A",
            labelColor: "rgba(24,20,16,0.55)",
            watermarkColor: "rgba(24,20,16,0.32)",
            titleFontClass: "font-mono uppercase tracking-widest",
            bodyFontClass: "font-mono uppercase",
            bodyWeightClass: "font-medium",
            readability: "light-bg",
            treatment: { vignette: 0.08, grain: 0.22 },
        },
        {
            id: "carbon-copy",
            label: "Carbon Copy",
            hueLane: "ink-on-paper",
            background: "linear-gradient(180deg, #1a1714 0%, #221d18 100%)",
            textColor: "#F2E5C8",
            accentColor: "#E8B560",
            labelColor: "rgba(242,229,200,0.62)",
            watermarkColor: "rgba(242,229,200,0.34)",
            titleFontClass: "font-mono uppercase tracking-widest",
            bodyFontClass: "font-mono uppercase",
            readability: "dark-bg",
            treatment: { vignette: 0.32, grain: 0.26 },
        },
    ],
};

// 13. NEWSPAPER — BREAKING NEWS headline format
export const NEWSPAPER = {
    id: "newspaper",
    label: "Newspaper",
    description: "Headline-driven. BREAKING NEWS. The voice of front-page urgency.",
    variants: [
        {
            id: "newsprint",
            label: "Newsprint",
            hueLane: "newsprint-cream",
            background: "linear-gradient(180deg, #F6EFE2 0%, #E8DEC8 100%)",
            textColor: "#0A0908",
            accentColor: "#B0321A",
            labelColor: "rgba(10,9,8,0.62)",
            watermarkColor: "rgba(10,9,8,0.30)",
            titleFontClass: "font-condensed uppercase",
            bodyFontClass: "font-serif",
            bodyWeightClass: "font-medium",
            readability: "light-bg",
            treatment: { vignette: 0.10, grain: 0.30 },
        },
        {
            id: "midnight-edition",
            label: "Midnight Edition",
            hueLane: "tabloid-noir",
            background: "linear-gradient(180deg, #0a0a0a 0%, #161616 100%)",
            textColor: "#F4EDE0",
            accentColor: "#E84A2A",
            labelColor: "rgba(244,237,224,0.62)",
            watermarkColor: "rgba(244,237,224,0.32)",
            titleFontClass: "font-condensed uppercase",
            bodyFontClass: "font-serif",
            readability: "dark-bg",
            treatment: { vignette: 0.42, grain: 0.28 },
        },
    ],
};

// 14. TICKET — concert / travel / memory stub format
export const TICKET = {
    id: "ticket",
    label: "Ticket",
    description: "Stub-format. Admit one — to a feeling, a moment, a memory.",
    variants: [
        {
            id: "concert-cream",
            label: "Concert · Cream",
            hueLane: "cream-warm",
            background: "linear-gradient(180deg, #F7E9C8 0%, #E8C898 100%)",
            textColor: "#1F1612",
            accentColor: "#9A2D1A",
            labelColor: "rgba(31,22,18,0.65)",
            watermarkColor: "rgba(31,22,18,0.32)",
            titleFontClass: "font-condensed uppercase",
            bodyFontClass: "font-display uppercase tracking-tight",
            bodyWeightClass: "font-medium",
            readability: "light-bg",
            treatment: { vignette: 0.18, grain: 0.20 },
        },
        {
            id: "travel-night",
            label: "Travel · Night",
            hueLane: "midnight-blue",
            background: "linear-gradient(180deg, #0a1228 0%, #1a2848 100%)",
            textColor: "#F2EAD8",
            accentColor: "#E8C870",
            labelColor: "rgba(242,234,216,0.65)",
            watermarkColor: "rgba(242,234,216,0.32)",
            titleFontClass: "font-condensed uppercase",
            bodyFontClass: "font-display uppercase tracking-tight",
            readability: "dark-bg",
            treatment: { vignette: 0.35, grain: 0.22 },
        },
    ],
};

// =====================================================================
// iter-104 — LETTER FAMILY (5 new packs from user-supplied references)
// Image-driven backgrounds for handcrafted intimacy. Two go free
// (love_letter, pen_paper); three stay paid (vow, forever, scrapbook).
// =====================================================================

// 15. LOVE LETTER — pink heart envelope / blush paper · FREE
export const LOVE_LETTER = {
    id: "love_letter",
    label: "Love Letter",
    description: "Blush envelope on cream — for the tender drafts and confessions.",
    variants: [
        {
            id: "blush-envelope",
            label: "Blush Envelope",
            hueLane: "blush-cream",
            background: "#F4DCD0 url('/pack_bg/love_letter.webp') center/cover no-repeat",
            textColor: "#3A1F26",
            accentColor: "#D17B8C",
            labelColor: "rgba(58,31,38,0.62)",
            watermarkColor: "rgba(58,31,38,0.32)",
            titleFontClass: "font-serif-warm italic",
            bodyFontClass: "font-serif-warm",
            bodyWeightClass: "font-normal",
            readability: "light-bg",
            treatment: { vignette: 0.10, grain: 0.10, scrim: "rgba(244,220,208,0.32)" },
            motion: { type: "breathe", intensity: "subtle" },
        },
    ],
};

// 16. PEN & PAPER — lined notebook · handwritten ink · FREE
export const PEN_PAPER = {
    id: "pen_paper",
    label: "Pen & Paper",
    description: "Lined paper, ballpoint blue. The first-draft handwriting feel.",
    variants: [
        {
            id: "ballpoint-blue",
            label: "Ballpoint Blue",
            hueLane: "paper-ink",
            background: "#FBFBF7 url('/pack_bg/pen_paper.webp') center/cover no-repeat",
            textColor: "#1E2A6E",
            accentColor: "#E47B9A",
            labelColor: "rgba(30,42,110,0.62)",
            watermarkColor: "rgba(30,42,110,0.32)",
            // Handwritten typeface — Caveat is loaded by Tailwind; fallback chain inside the class.
            titleFontClass: "font-handwritten",
            bodyFontClass: "font-handwritten",
            bodyWeightClass: "font-normal",
            readability: "light-bg",
            treatment: { vignette: 0.06, grain: 0.06 },
        },
    ],
};

// 17. VOW — pearl crystal frame on silk · PAID
export const VOW = {
    id: "vow",
    label: "Vow",
    description: "Pearl crystal on silk. For promises, anniversaries, marriage.",
    variants: [
        {
            id: "pearl-silk",
            label: "Pearl on Silk",
            hueLane: "pearl-cream",
            background: "#F4EEE8 url('/pack_bg/vow.webp') center/cover no-repeat",
            textColor: "#3A2E2A",
            accentColor: "#B89972",
            labelColor: "rgba(58,46,42,0.62)",
            watermarkColor: "rgba(58,46,42,0.32)",
            titleFontClass: "font-serif-formal",
            bodyFontClass: "font-serif",
            bodyWeightClass: "font-normal",
            readability: "light-bg",
            treatment: { vignette: 0.18, grain: 0.05, scrim: "rgba(244,238,232,0.42)" },
            motion: { type: "shimmer", intensity: "subtle" },
        },
    ],
};

// 18. FOREVER — wedding rings + botanical gold frame · PAID
export const FOREVER = {
    id: "forever",
    label: "Forever",
    description: "Gold rings on cream silk. For weddings and milestone declarations.",
    variants: [
        {
            id: "gold-rings",
            label: "Gold Rings",
            hueLane: "cream-gold",
            background: "#F4E8D0 url('/pack_bg/forever.webp') center/cover no-repeat",
            textColor: "#3A2A1A",
            accentColor: "#C9A05A",
            labelColor: "rgba(58,42,26,0.65)",
            watermarkColor: "rgba(58,42,26,0.32)",
            titleFontClass: "font-serif-formal italic",
            bodyFontClass: "font-serif",
            bodyWeightClass: "font-normal",
            readability: "light-bg",
            treatment: { vignette: 0.16, grain: 0.06, scrim: "rgba(244,232,208,0.40)" },
            motion: { type: "glow", intensity: "subtle" },
        },
    ],
};

// 19. SCRAPBOOK — torn notebook, washi, doodles · PAID
export const SCRAPBOOK = {
    id: "scrapbook",
    label: "Scrapbook",
    description: "Torn pages, washi tape, doodles. School-crush nostalgia.",
    variants: [
        {
            id: "washi-pages",
            label: "Washi & Pages",
            hueLane: "cream-collage",
            background: "#F6EFE0 url('/pack_bg/scrapbook.webp') center/cover no-repeat",
            textColor: "#2A2418",
            accentColor: "#E47B9A",
            labelColor: "rgba(42,36,24,0.62)",
            watermarkColor: "rgba(42,36,24,0.32)",
            titleFontClass: "font-handwritten",
            bodyFontClass: "font-handwritten",
            bodyWeightClass: "font-normal",
            readability: "light-bg",
            treatment: { vignette: 0.12, grain: 0.10, scrim: "rgba(246,239,224,0.34)" },
        },
    ],
};



// =====================================================================
// Registry — list order = display order in pickers
// =====================================================================
export const STYLE_PACKS = {
    cinematic: CINEMATIC,
    romantic: ROMANTIC,
    minimal: MINIMAL,
    bold_gradient: BOLD_GRADIENT,
    faith: FAITH,
    vintage: VINTAGE,
    brutalist: BRUTALIST,
    polaroid: POLAROID_V2,
    watercolor: WATERCOLOR_V2,
    playful: PLAYFUL,
    luxury: LUXURY,
    receipt: RECEIPT,
    newspaper: NEWSPAPER,
    ticket: TICKET,
    // iter-104 — Letter family: handcrafted backgrounds from user-supplied
    // references. Two open to free (love_letter, pen_paper) to richen the
    // free tier per direction; vow / forever / scrapbook stay paid.
    love_letter: LOVE_LETTER,
    pen_paper: PEN_PAPER,
    vow: VOW,
    forever: FOREVER,
    scrapbook: SCRAPBOOK,
};

export const STYLE_PACK_IDS = Object.keys(STYLE_PACKS);

// iter-93 — per-pack halo glow color. Falls back to warm champagne for
// packs we haven't surveyed. Used by the public viewer to tint the
// always-on radial premium light behind the card.
export function getPackAccent(packId) {
    const pack = STYLE_PACKS[packId];
    if (!pack || !pack.variants || !pack.variants.length) return "rgba(232,213,168,0.22)";
    const hex = pack.variants[0].accentColor || "#E8D5A8";
    // Convert "#RRGGBB" → rgba with 0.22 alpha for halo glow.
    const h = hex.replace("#", "");
    if (h.length === 6) {
        const r = parseInt(h.slice(0, 2), 16);
        const g = parseInt(h.slice(2, 4), 16);
        const b = parseInt(h.slice(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, 0.22)`;
    }
    return "rgba(232,213,168,0.22)";
}

// =====================================================================
// Lane → recommended packs (smart routing on Home)
//
// 4 packs surfaced for free users based on the inferred emotion lane.
// Premium users see ALL 14 packs at once.
// =====================================================================
export const LANE_TO_PACKS = {
    heart:    ["romantic", "cinematic", "watercolor", "polaroid"],
    healing:  ["faith", "watercolor", "minimal", "romantic"],
    faith:    ["faith", "luxury", "minimal", "vintage"],
    drive:    ["bold_gradient", "brutalist", "cinematic", "luxury"],
    funny:    ["receipt", "newspaper", "playful", "bold_gradient"],
    life:     ["ticket", "minimal", "polaroid", "cinematic"],
    civic:    ["newspaper", "brutalist", "bold_gradient", "minimal"],
    banter:   ["receipt", "playful", "newspaper", "ticket"],
};

export const DEFAULT_PACKS = [
    "cinematic", "minimal", "romantic", "bold_gradient",
    "faith", "vintage", "brutalist", "polaroid",
    "watercolor", "playful", "luxury",
    "receipt", "newspaper", "ticket",
];

/** Suggest 4 packs for a given lane, falling back to defaults. */
export function recommendPacksFor(lane) {
    if (lane && LANE_TO_PACKS[lane]) return LANE_TO_PACKS[lane];
    return DEFAULT_PACKS.slice(0, 4);
}

// =====================================================================
// Recipe resolution — single entry point so every consumer agrees.
//
// Resolution order:
//   1. Explicit recipeId argument          (premium swap from drawer)
//   2. localStorage `moodcard.recipe.<id>` (premium swap persisted)
//   3. card.recipe_id from backend        (the pack-aware auto-pick)
//   4. Deterministic from card.id          (so reloads stay stable)
//   5. First variant of resolved pack
// =====================================================================
function _hash(s) {
    if (!s || typeof s !== "string") return 0;
    let h = 2166136261;
    for (let i = 0; i < s.length; i += 1) {
        h ^= s.charCodeAt(i);
        h = (h * 16777619) >>> 0;
    }
    return h;
}

export function getRecipeOverride(moodId) {
    if (!moodId) return null;
    try {
        return window.localStorage.getItem(`moodcard.recipe.${moodId}`) || null;
    } catch {
        return null;
    }
}

export function setRecipeOverride(moodId, recipeKey) {
    if (!moodId) return;
    try {
        if (recipeKey) window.localStorage.setItem(`moodcard.recipe.${moodId}`, recipeKey);
        else window.localStorage.removeItem(`moodcard.recipe.${moodId}`);
    } catch { /* ignore */ }
}

/** Resolve a (pack, variant) recipe object. Returns null if no recipe
 *  applies (caller falls back to legacy packTreatment). recipeKey is
 *  shaped "<pack>.<variant>" — e.g. "cinematic.golden-hour".
 *
 *  Special case: when the override is `<pack>.__custom__`, a synthetic
 *  recipe is returned that uses the card's `custom_background_url` as
 *  the background image (the URL is read by the caller and passed via
 *  the `customBackgroundUrl` arg). */
export function resolveRecipe({ moodId, pack, recipeId, recipeOverride, customBackgroundUrl }) {
    const packId = (pack || "").toLowerCase();
    const packDef = STYLE_PACKS[packId];
    if (!packDef) return null; // legacy pack, no recipe support yet

    // 1. Explicit override (drawer just wrote it)
    let key = recipeOverride || getRecipeOverride(moodId);

    // 1a. Custom-background sentinel — wrap user upload as a recipe.
    if (key && isCustomRecipeKey(key) && customBackgroundUrl) {
        return buildCustomRecipe(packId, customBackgroundUrl);
    }

    // 1b. Override matches the CURRENT pack — apply directly.
    if (key && key.startsWith(`${packId}.`)) {
        const vid = key.slice(packId.length + 1);
        const v = packDef.variants.find((x) => x.id === vid);
        if (v) return { pack: packDef, variant: v, key };
    }

    // 1c. Override references a DIFFERENT pack — let it win.
    // This is the "PackIterationStrip swap" case: user tapped "minimal"
    // on a card originally generated as "cinematic", we want the visual
    // to actually swap. Look up the override's pack prefix and use it.
    if (key && key.includes(".")) {
        const overridePackId = key.slice(0, key.indexOf(".")).toLowerCase();
        const overridePackDef = STYLE_PACKS[overridePackId];
        if (overridePackDef) {
            const vid = key.slice(overridePackId.length + 1);
            const v = overridePackDef.variants.find((x) => x.id === vid);
            if (v) return { pack: overridePackDef, variant: v, key };
        }
    }

    // 2. Backend-supplied recipe_id on the card
    if (recipeId) {
        const v = packDef.variants.find((x) => x.id === recipeId);
        if (v) return { pack: packDef, variant: v, key: `${packId}.${v.id}` };
    }

    // 3. Deterministic from mood_id — stable across reloads
    if (moodId) {
        const idx = _hash(moodId) % packDef.variants.length;
        const v = packDef.variants[idx];
        return { pack: packDef, variant: v, key: `${packId}.${v.id}` };
    }

    // 4. Fallback to first variant
    return { pack: packDef, variant: packDef.variants[0], key: `${packId}.${packDef.variants[0].id}` };
}

export function isStylePackV2(pack) {
    return !!STYLE_PACKS[(pack || "").toLowerCase()];
}

// ── Anti-repetition history (Phase 2 spec) ───────────────────────────
// Track the last 12 recipe keys this user has seen across all cards in
// localStorage. Shuffle + auto-pick use this to bias AWAY from recent
// recipes. Persists silently — no UI, just less déjà vu.
const _HISTORY_KEY = "moodcard.recipe_history.v1";
const _HISTORY_LIMIT = 12;

export function rememberRecipe(key) {
    if (!key) return;
    try {
        const raw = window.localStorage.getItem(_HISTORY_KEY);
        const list = raw ? JSON.parse(raw) : [];
        const filtered = [key, ...list.filter((k) => k !== key)].slice(0, _HISTORY_LIMIT);
        window.localStorage.setItem(_HISTORY_KEY, JSON.stringify(filtered));
    } catch { /* ignore — never block render on history */ }
}

export function getRecentRecipes() {
    try {
        const raw = window.localStorage.getItem(_HISTORY_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

/**
 * Pick a random recipe key from `pack` that:
 *   • isn't `currentVariantId`
 *   • is NOT in the last-12 history if a non-recent candidate exists
 *
 * Premium users → drawn from all 4 variants.
 * Free users    → drawn from the 1 free variant only.
 * Returns key as "<pack>.<variant>" or null when there's no alternative.
 */
export function pickShuffleRecipe(packId, currentVariantId, { isPremium = false } = {}) {
    const pack = STYLE_PACKS[(packId || "").toLowerCase()];
    if (!pack || !pack.variants.length) return null;
    const accessible = isPremium ? pack.variants : pack.variants.slice(0, 1);
    const pool = accessible.filter((v) => v.id !== currentVariantId);
    if (!pool.length) return null;
    const recent = new Set(getRecentRecipes());
    const fresh = pool.filter((v) => !recent.has(`${pack.id}.${v.id}`));
    const candidates = fresh.length > 0 ? fresh : pool;  // fall back if all are recent
    const v = candidates[Math.floor(Math.random() * candidates.length)];
    return { pack, variant: v, key: `${pack.id}.${v.id}` };
}

// ── Custom user-uploaded background sentinel ────────────────────────
// When the user uploads their own background image, we record the
// recipe override as "<pack>.__custom__" so resolveRecipe knows to
// return a synthetic recipe that wraps the custom URL while keeping
// the pack's typography + text colors intact.
export const CUSTOM_RECIPE_VARIANT_ID = "__custom__";

/** Returns true when the resolved key is the custom-bg sentinel. */
export function isCustomRecipeKey(key) {
    return typeof key === "string" && key.endsWith(`.${CUSTOM_RECIPE_VARIANT_ID}`);
}

/** Build a synthetic recipe that uses `customUrl` as its background but
 *  inherits typography + text colors from the FIRST variant of the pack.
 *  Falls back to the cinematic pack if `packId` is unknown. */
export function buildCustomRecipe(packId, customUrl) {
    const pack = STYLE_PACKS[(packId || "").toLowerCase()] || CINEMATIC;
    const base = pack.variants[0];
    const variant = {
        ...base,
        id: CUSTOM_RECIPE_VARIANT_ID,
        label: "Your photo",
        // CSS background image with cover/center positioning + soft dark
        // veil so dark recipe text stays readable on light photos.
        background: `linear-gradient(180deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.38) 100%), url("${customUrl}") center/cover no-repeat, #1a1715`,
        readability: "busy-bg",  // force glass panel since photos vary wildly
        _isCustom: true,
    };
    return { pack, variant, key: `${pack.id}.${CUSTOM_RECIPE_VARIANT_ID}` };
}
